import { useSyncExternalStore } from 'react';

// Layout-Umschaltung per JavaScript, nicht per CSS.
//
// Die naheliegende Lösung wäre, beide Layouts zu rendern und eines per
// `hidden md:flex` wegzublenden. Das geht hier nicht: die Einführungstour
// sucht ihre Anker mit `document.querySelector('[data-tour="…"]')`, und
// bei zwei Treffern gewinnt der erste im Dokument — womöglich der
// unsichtbare. driver.js würde dann auf ein `display:none`-Element zeigen,
// dessen getBoundingClientRect() 0,0 liefert; der Scheinwerfer landete in
// der linken oberen Ecke. Genau diese Fehlerklasse steht in CLAUDE.md.
//
// Deshalb ist immer nur EIN Layout im Baum, und der Anker-Wächter kann
// beide getrennt prüfen (src/test/tourAnker.test.tsx).

/** Ab hier abwärts: Bottom-Navigation statt Seitenspalte. */
export const MOBIL_MAX_PX = 767;
/** Ab hier abwärts: Karten statt Tabelle. */
export const SCHMAL_MAX_PX = 639;

const MOBIL_QUERY = `(max-width: ${MOBIL_MAX_PX}px)`;
const SCHMAL_QUERY = `(max-width: ${SCHMAL_MAX_PX}px)`;

/**
 * Ein Abonnement je Abfrage, auf Modulebene gehalten.
 *
 * useSyncExternalStore hängt sich neu ein, sobald sich die
 * subscribe-Funktion ändert. Würde sie bei jedem Render neu entstehen,
 * liefe bei jedem Render ein ab- und ein anmelden.
 */
const abos = new Map<string, (aendern: () => void) => () => void>();

function abo(query: string): (aendern: () => void) => () => void {
  const vorhanden = abos.get(query);
  if (vorhanden) return vorhanden;
  const neu = (aendern: () => void): (() => void) => {
    if (typeof window.matchMedia !== 'function') return () => {};
    const liste = window.matchMedia(query);
    liste.addEventListener('change', aendern);
    return () => liste.removeEventListener('change', aendern);
  };
  abos.set(query, neu);
  return neu;
}

function passt(query: string): boolean {
  // Fehlt matchMedia (alte Umgebung, Testlauf ohne Stub), gilt das
  // Desktop-Layout. Es ist das vollständigere von beiden — ein falsch
  // erkanntes Mobilgerät verlöre die Seitenspalte, umgekehrt bleibt
  // höchstens die Bottom-Navigation aus.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/** Schmaler als 768 px — Bottom-Navigation statt Seitenspalte. */
export function useIstMobil(): boolean {
  return useSyncExternalStore(
    abo(MOBIL_QUERY),
    () => passt(MOBIL_QUERY),
    () => false
  );
}

/** Schmaler als 640 px — Karten statt Tabelle. */
export function useIstSchmal(): boolean {
  return useSyncExternalStore(
    abo(SCHMAL_QUERY),
    () => passt(SCHMAL_QUERY),
    () => false
  );
}

/**
 * Ein Schreibtisch-Arbeitsplatz: breiter Schirm UND eine Maus (A14.5).
 *
 * WARUM NICHT EINFACH DIE BREITE
 *   Ein iPad quer misst 1180 Pixel und waere nach jeder Breiten-Regel
 *   „Desktop". Fuer den Uebergabe-QR ist das genau der falsche Fall: Er
 *   soll VOM Monitor abgescannt werden, und wer das Tablet in der Hand
 *   hat, braucht ihn nicht auf demselben Geraet.
 *
 *   `(pointer: fine)` fragt nach einem genauen Zeigegeraet — Maus oder
 *   Stift. Ein Finger ist `coarse`. Das ist der naechste Wert an
 *   „steht am Schreibtisch", den ein Browser hergibt.
 *
 * Ohne `matchMedia` gilt hier NICHT das Desktop-Layout, sondern das
 * Gegenteil: der QR ist eine Zugabe, und eine Zugabe, die im Zweifel
 * fehlt, kostet niemanden etwas. Ein Layout, das im Zweifel fehlt,
 * schon.
 */
const SCHREIBTISCH_QUERY = '(min-width: 1024px) and (pointer: fine)';

export function useIstSchreibtisch(): boolean {
  return useSyncExternalStore(
    abo(SCHREIBTISCH_QUERY),
    () => passt(SCHREIBTISCH_QUERY),
    () => false
  );
}

/**
 * Gibt es hier ein genaues Zeigegeraet — also eine Maus? (A35)
 *
 * Gebraucht fuer den Handscanner am Buero-PC: Das Eingabefeld im
 * Scan-Fenster bekommt nur dort von selbst den Fokus. Am Tablet wuerde
 * ein Feld mit Fokus die Bildschirmtastatur aufziehen, und die deckte
 * genau das zu, worauf es dort ankommt — die Kamera.
 *
 * NICHT `useIstSchreibtisch()`: Dort steckt zusaetzlich `min-width:
 * 1024px`. Ein PC-Fenster auf halbem Schirm ist schmaler und bleibt
 * doch ein PC mit Maus und Handscanner.
 *
 * Ohne `matchMedia` gilt `false` — dann bekommt niemand ungefragt den
 * Fokus. Falscher Fokus kostet mehr als fehlender: er zieht auf einem
 * Tastaturgeraet die Tastatur auf, und auf dem Tablet verdeckt sie die
 * Kamera.
 */
const ZEIGER_FEIN_QUERY = '(pointer: fine)';

export function useZeigerFein(): boolean {
  return useSyncExternalStore(
    abo(ZEIGER_FEIN_QUERY),
    () => passt(ZEIGER_FEIN_QUERY),
    () => false
  );
}
